import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PackageCheck, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { useStoreData } from '../hooks/useStoreData';
import { useCart } from '../contexts/CartContext';
import Header from '../components/Header';
import Cart from '../components/Cart';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const ExchangePolicy = () => {
  const navigate = useNavigate();
  const { settings } = useStoreData();
  const { getCartCount } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Exchange & Return Policy | Rizzahra"
        description="Learn about Rizzahra's exchange and return policy. Easy returns within 7 days. Your satisfaction is our priority."
        keywords="exchange policy, return policy, abaya returns, customer satisfaction"
        url="/exchange-policy"
      />
      {/* Main Header */}
      <Header
        cartCount={getCartCount()}
        onCartClick={() => setIsCartOpen(true)}
        onAdminClick={() => navigate('/admin')}
      />

      {/* Page Header */}
      <header className="bg-[#4a1a1f] text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold uppercase tracking-wider">Exchange & Return Policy</h1>
          <p className="text-gray-300 mt-2">Your satisfaction is our priority</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <p className="text-gray-700 leading-relaxed">
              At {settings?.storeName || 'Rizzahra'}, we want you to be completely satisfied with your purchase.
              If for any reason you're not happy with your order, we're here to help. Please read our exchange
              and return policy carefully to understand your options.
            </p>
          </div>

          {/* Exchange Period */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                <RefreshCw className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#4a1a1f]">7-Day Exchange Policy</h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>
                We offer a <strong>7-day exchange policy</strong> from the date of delivery. If you receive a
                product that doesn't meet your expectations, you may exchange it for a different size, color,
                or product of equal or greater value.
              </p>
              <p>
                <strong>Important:</strong> The 7-day period starts from the date of delivery, not the date
                of order placement.
              </p>
            </div>
          </div>

          {/* Eligibility Criteria */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-6">Eligibility for Exchange/Return</h2>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                <CheckCircle size={20} />
                Items Eligible for Exchange
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Product is unused, unworn, and in its original condition</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>All original tags and labels are still attached</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Product is in its original packaging (if applicable)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>No signs of wear, washing, or alteration</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Request is made within 7 days of delivery</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Product received is defective or damaged</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Wrong item was delivered</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                <AlertCircle size={20} />
                Items NOT Eligible for Exchange
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-1">✗</span>
                  <span>Products with removed or damaged tags</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-1">✗</span>
                  <span>Items that have been worn, washed, or altered</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-1">✗</span>
                  <span>Items showing signs of use or damage caused by the customer</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-1">✗</span>
                  <span>Sale or clearance items (unless defective)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-1">✗</span>
                  <span>Custom or personalized orders</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 mt-1">✗</span>
                  <span>Returns requested after the 7-day window</span>
                </li>
              </ul>
            </div>
          </div>

          {/* How to Initiate Exchange */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                <PackageCheck className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#4a1a1f]">How to Request an Exchange</h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-lg mb-2">Step 1: Contact Us</h3>
                <p className="mb-2">Reach out to our customer service team within 7 days of delivery:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  {settings?.contactInfo?.phone && (
                    <li>Phone: {settings.contactInfo.phone}</li>
                  )}
                  {settings?.contactInfo?.email && (
                    <li>Email: {settings.contactInfo.email}</li>
                  )}
                  <li>WhatsApp: Available during business hours</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">Step 2: Provide Information</h3>
                <p>When contacting us, please have the following ready:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Order number</li>
                  <li>Product name/SKU</li>
                  <li>Reason for exchange</li>
                  <li>Photos (if product is defective or damaged)</li>
                  <li>Preferred exchange item (size/color/product)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">Step 3: Return Shipping</h3>
                <p>Once your exchange request is approved:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>We'll provide you with return instructions</li>
                  <li>Pack the item securely in its original packaging</li>
                  <li>Ship the item back to our warehouse</li>
                  <li><strong>Important:</strong> Return shipping costs are the customer's responsibility, except for defective or incorrect items</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">Step 4: Processing</h3>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Once we receive your return, we'll inspect it within 2-3 business days</li>
                  <li>If approved, your exchange will be processed immediately</li>
                  <li>If exchanging for a more expensive item, you'll need to pay the difference</li>
                  <li>If exchanging for a less expensive item, we'll refund the difference</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Refund Policy */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Refund Policy</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We primarily offer <strong>exchanges rather than refunds</strong>. However, refunds may be
                issued in the following cases:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Product received is significantly different from what was ordered</li>
                <li>Product is defective and no suitable replacement is available</li>
                <li>We are unable to fulfill your exchange request</li>
              </ul>
              <p>
                <strong>Refund Processing:</strong> Approved refunds will be processed within 5-7 business
                days to your original payment method. For Cash on Delivery orders, refunds will be issued
                via bank transfer.
              </p>
              <p className="font-semibold">
                Note: Shipping charges are non-refundable except in cases where we shipped a defective or
                incorrect product.
              </p>
            </div>
          </div>

          {/* Damaged or Defective Items */}
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-bold text-amber-900 mb-3">Received a Damaged or Defective Product?</h3>
            <p className="text-amber-800 mb-3">
              We sincerely apologize if you received a damaged or defective item. Please contact us
              immediately (within 48 hours of delivery) with:
            </p>
            <ul className="list-disc list-inside ml-4 text-amber-800 space-y-1">
              <li>Clear photos of the defect or damage</li>
              <li>Your order number</li>
              <li>Description of the issue</li>
            </ul>
            <p className="text-amber-800 mt-3">
              We'll arrange for a replacement or full refund at no additional cost to you, including
              return shipping.
            </p>
          </div>

          {/* Contact Section */}
          <div className="bg-[#4a1a1f] text-white rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold mb-3">Need Help?</h3>
            <p className="text-gray-300 mb-6">
              Our customer service team is here to assist you with any questions about exchanges or returns.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {settings?.contactInfo?.phone && (
                <a
                  href={`tel:${settings.contactInfo.phone}`}
                  className="bg-white text-[#4a1a1f] px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
                >
                  Call: {settings.contactInfo.phone}
                </a>
              )}
              {settings?.contactInfo?.email && (
                <a
                  href={`mailto:${settings.contactInfo.email}`}
                  className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-[#4a1a1f] transition-colors"
                >
                  Email Us
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default ExchangePolicy;
